import React from 'react';
import { Zap, ChevronLeft, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ChangeEntry {
  id: string;
  version: string;
  title: string;
  date: string;
  type: 'feature' | 'fix' | 'improvement';
  description: string;
  changes: string[];
}

const CHANGELOG: ChangeEntry[] = [
  // ChangeEntry field list (one owned changelog entry):
  //   id: string;
  //   version: string;
  //   title: string;
  //   date: string;
  //   type: 'feature' | 'fix' | 'improvement';
  //   description: string;
  //   changes: string[];
  // TODO: confirm id/version — latest git commit is v1.31, but previous array ends at 1.22.
  // TODO: confirm ship date before publishing.
  {
    id: '31', version: '1.31', date: 'TBD - ship date', type: 'feature',
    title: 'Family Safety & Location Hub',
    description: 'Live family location sharing, geofence zones, and a dashboard location widget.',
    changes: [
      'Live location sharing — household members who opt in appear on the member map with real-time position pins',
      'Distance & time-ago — each member card shows how far away they are and when their location last updated',
      'Nearby check-in — a floating action button lets members check in when they arrive near a home geofence',
      'Geofence zones — place, name, and size circular zones on the map with separate notify-on-arrive and notify-on-depart toggles',
      'Location dashboard widget — the home screen surfaces who is currently nearby or en route',
    ],
  },
  {
    id: '22', version: '1.22', date: 'Jun 2, 2026', type: 'improvement',
    title: 'Referral System & Creator Program',
    description: 'Every user now has a unique personal referral code, and creators can apply to the program directly inside the app.',
    changes: [
      'Personal referral code — every account gets a unique 7-character code shareable on its own, no URL required',
      'Copy Code button — one tap copies just your code to the clipboard alongside the existing Copy Link option',
      'In-app Creator Program application — fill out a short form (social links, audience size, reason) without leaving EverydayOS',
      'Application status tracking — the Become a Creator card updates in real time: Under Review, Approved, or Not Approved',
      'Sidebar collapse state now persists across sessions',
    ],
  },
  {
    id: '21', version: '1.21', date: 'May 28, 2026', type: 'feature',
    title: 'Co-Parenting Intelligence: Legal-Grade Communications',
    description: 'Real-time co-parenting messaging with AI tone analysis, legally exportable threads, and built-in conflict resolution tools.',
    changes: [
      'Tone Meter — every message is scored by AI in real time (calm → tense → hostile) to encourage constructive communication',
      'Export Thread — download a date-stamped, formatted PDF transcript of any message thread for legal or mediation use',
      'Conflict Detection — AI flags custody scheduling conflicts and suggests resolution options before they escalate',
      'Mediator Invitations — invite a neutral third party to join a co-parenting space with a dedicated mediator role',
      'Expense Receipts — attach receipt URLs directly to shared expense entries for transparent record-keeping',
    ],
  },
  {
    id: '20', version: '1.20', date: 'May 20, 2026', type: 'feature',
    title: 'Finance Depth: Budget Variance Engine',
    description: 'A full budgeting layer on top of your transactions — set monthly spending ceilings, track variance by category, and let AI flag unusual spend.',
    changes: [
      'Budget Setup — define monthly spending ceilings per category (housing, food, transport, utilities, entertainment, health, and more)',
      'Variance Dashboard — visual progress bars per category showing spend vs budget with colour-coded over/under indicators',
      'AI Transaction Categorisation — new transactions are automatically categorised by merchant name using Gemini',
      'Spending Anomaly Detection — AI reviews your last 90 days and surfaces up to 5 transactions that are unusually high for their category',
      'Rollover Budgets — any underspend in a category rolls over into next month\'s ceiling at a configurable percentage',
      'Month Navigation — scroll back through previous months\' variance reports',
    ],
  },
  {
    id: '19', version: '1.19', date: 'May 12, 2026', type: 'feature',
    title: 'Appliance Intelligence',
    description: 'Track every appliance in your home — warranties, service history, AI life expectancy predictions, and automatic CPSC recall monitoring.',
    changes: [
      'Appliance register — add make, model, purchase date, warranty expiry, and location for each appliance',
      'Service records — log repairs and maintenance with cost tracking; total lifetime service cost calculated automatically',
      'AI life prediction — Gemini estimates remaining useful life and flags appliances approaching end-of-life',
      'Automatic recall monitoring — daily background check against CPSC database; in-app alert created if your appliance is recalled',
      'Appliance widget — dashboard card showing appliance count, any active recall alerts, and total maintenance spend',
    ],
  },
  {
    id: '18', version: '1.18', date: 'May 5, 2026', type: 'improvement',
    title: 'Navigation Overhaul & Dashboard Widgets',
    description: 'A new always-visible sidebar replaces the icon rail, and four new dashboard widgets bring finance, meals, co-parenting, and elder care to your home screen.',
    changes: [
      'New sidebar navigation — full text labels always visible, collapsible to icon-only, state saved across sessions',
      'Finance Widget — monthly spend summary with budget status at a glance',
      'Meal Planner Widget — today\'s meals and next planned recipe shown on the dashboard',
      'Co-Parenting Widget — upcoming custody handovers and recent message tone summary',
      'Elder Care Widget — medication schedule and upcoming care appointments for monitored profiles',
      'Vehicle service indicators — cards now show amber/red warning when service is approaching or overdue',
    ],
  },
  {
    id: '17', version: '1.17', date: 'Apr 25, 2026', type: 'feature',
    title: 'First-Time Onboarding Experience',
    description: 'A guided setup flow gets new households up and running in minutes, with a template library and a persistent Getting Started checklist.',
    changes: [
      'Split wizard — household creators and joiners now get tailored setup flows rather than a one-size-fits-all screen',
      'Getting Started widget — checklist on the dashboard tracks completed onboarding steps and disappears once done',
      'Template library — pre-built household configurations (family home, student house, couple) auto-populate tasks, grocery categories, and bill reminders',
      'Invite deep-link handling — sharing an invite link opens the app directly to the join flow',
      'Auto-detect progress — the app detects what you\'ve already set up and skips those steps automatically',
    ],
  },
  {
    id: '16', version: '1.16', date: 'Apr 14, 2026', type: 'feature',
    title: 'Elder Care',
    description: 'A full care coordination module for households supporting elderly relatives — health records, medications, appointments, transport, and a simplified Elder Mode.',
    changes: [
      'Care profiles — create a dedicated profile for any elder with health history, emergency contacts, and DNR documentation',
      'Medication tracker — log medications with dosage, schedule, and adherence history; confirm doses from the home screen',
      'Appointment management — schedule and track medical and care appointments with transport assignments',
      'Care team — assign household members and external providers as care-admin, contributor, or viewer',
      'Elder Mode — a simplified, large-type home screen designed for elder users with quick access to check-in and medication confirmation',
      'Emergency Brief — printable one-page summary of medications, conditions, and emergency contacts',
    ],
  },
  {
    id: '15', version: '1.15', date: 'Apr 2, 2026', type: 'feature',
    title: 'Municipal Services & Community Hub',
    description: 'Subscribe to local council updates and receive in-app alerts when services affecting your area are posted.',
    changes: [
      'Council service feed — browse and search active municipal announcements, road closures, waste collection changes, and alerts',
      'Area subscriptions — subscribe to specific service types or postcode areas to filter the feed to what matters to you',
      'In-app push alerts — urgent council notices trigger a notification rather than waiting for you to check the feed',
      'Community Hub — a shared space for households to post and respond to neighbourhood-level announcements',
    ],
  },
  {
    id: '14', version: '1.14', date: 'Mar 18, 2026', type: 'feature',
    title: 'Home Intelligence: Budget Tracking, Recall Checks & Recipe Actions',
    description: 'Maintenance spend tracking with annual budget caps, CPSC recall checks per appliance, and recipe-to-plan and recipe-to-grocery flows in the meal planner.',
    changes: [
      'Maintenance Budget tab — YTD spend analysis, top cost items, annual budget cap with progress bar and over-budget alert',
      'Appliance CPSC Recall button — one-click opens recall search pre-filled with make and model',
      'Recipe name search — filter library by name alongside existing tag chips',
      'Add to Meal Plan — pick day and meal slot directly from any recipe card',
      'Add to Grocery List — bulk-add all recipe ingredients to household grocery list',
      'Warranty expiry Cloud Function — daily push notifications 30 days before appliance warranty expires',
    ],
  },
  {
    id: '13', version: '1.13', date: 'Mar 18, 2026', type: 'feature',
    title: 'Members Hub, Property Tracker & Mobile Polish',
    description: 'Household member roster with invite codes, property equity tracking, and AppTheme consistency across mobile screens.',
    changes: [
      'Analytics route wired — household analytics now accessible via /analytics',
      'XP scores added to Household Settings member roster',
      'Mobile maintenance and calendar screens updated to AppTheme design tokens',
    ],
  },
  {
    id: '12', version: '1.12', date: 'Mar 18, 2026', type: 'feature',
    title: 'Mobile Settings Revamp & Household Invite Flow',
    description: 'Complete redesign of the mobile settings experience with the brand design system, plus a new Join Household screen.',
    changes: [
      'Revamped mobile Settings screen with AppTheme colors, household info and invite code display',
      'New Join Household screen — enter invite code to join from mobile',
      'AppTheme tokens applied across contacts, notifications, and bills screens',
      'Changelog now shows real version history instead of empty Firestore reads',
    ],
  },
  {
    id: '11', version: '1.11', date: 'Mar 18, 2026', type: 'feature',
    title: 'Daily Brief Widget, Mobile Dashboard & Bill Payments',
    description: 'The AI-powered Daily Brief is now a dashboard widget, the mobile dashboard is fully revamped, and bill payments are trackable.',
    changes: [
      'Daily Brief widget added to dashboard — AI summary of your day with weather, events, and tasks',
      'Mobile dashboard fully revamped: AppTheme, time-based greeting, stat chips, correct Firestore paths',
      'Bill payment tracking — mark bills as paid with running history',
      'Recipes screen wired into mobile More grid',
    ],
  },
  {
    id: '10', version: '1.10', date: 'Mar 18, 2026', type: 'feature',
    title: 'Gamification Pipeline & Achievements',
    description: 'Full XP system wired end-to-end: task completion awards XP, Achievements page with badge milestones, monthly leaderboard.',
    changes: [
      'XP awarded on task completion (P1=30, P2=20, P3=10 XP) via non-blocking Cloud Function call',
      'Achievements page with 8 badge milestones and AI motivation tips',
      'Monthly leaderboard populated by awardMemberPoints Cloud Function with monthly reset',
      'Mobile gamification screen with personal XP card and household rankings',
    ],
  },
  {
    id: '9', version: '1.9', date: 'Mar 18, 2026', type: 'improvement',
    title: 'Design System & Flutter AppTheme',
    description: 'Full design token system applied across web and mobile — brand blue, accent yellow, dark surfaces.',
    changes: [
      'Brand design tokens: #5e88fc primary blue, #ffde5a accent yellow, #0f1117 base dark',
      'Layout navigation fully rebranded from indigo to brand blue with matching shadows',
      'LandingPage rebuilt with 12-slide feature carousel, 3-tier pricing, and gradient hero',
      'Flutter AppTheme with light/dark variants applied across all mobile screens',
    ],
  },
  {
    id: '8', version: '1.8', date: 'Mar 15, 2026', type: 'feature',
    title: 'Unified AI Action Dispatcher',
    description: 'Single Cloud Function routes 7 household AI tasks with Gemini 2.0 Flash, rate-limited to 50 calls/day.',
    changes: [
      'AI action dispatcher routes: task breakdown, priority advice, chore schedule generation',
      'Grocery item categorization, 7-day meal plan generation, maintenance priority assessment',
      'Rate limiting: 50 AI calls/day per household tracked in ai_context collection',
    ],
  },
  {
    id: '7', version: '1.7', date: 'Mar 12, 2026', type: 'feature',
    title: 'Household Expansion — Pets, Vehicles, Bills & FairShare',
    description: 'Five new household modules covering the full home management spectrum.',
    changes: [
      'Pet management with care tasks and medical records',
      'Vehicle tracking with mileage log and shared booking system',
      'Bills & subscriptions with monthly spend analytics and due-date calendar',
      'FairShare labor balance — fair contribution scoring across household members',
      'Property equity tracker for homeowners',
    ],
  },
  {
    id: '6', version: '1.6', date: 'Mar 10, 2026', type: 'feature',
    title: 'Dashboard Evolution & First-Run Experience',
    description: 'Modular drag-and-drop dashboard extended with new widgets and a guided household setup wizard.',
    changes: [
      'Bills Overview and Pet Care dashboard widgets',
      'Household Setup Wizard — 4-step flow for new users with invite code generation',
      'Per-route error boundaries wrapping all authenticated routes',
      'Analytics subscription spend breakdown by category',
      'Mobile Documents screen with file URL launch',
    ],
  },
  {
    id: '5', version: '1.5', date: 'Mar 8, 2026', type: 'feature',
    title: 'Mobile & AI Parity',
    description: 'Mobile app reaches feature parity with web, plus payment infrastructure and referral system.',
    changes: [
      'AI Assistant with natural language task creation on web and mobile',
      'Mobile push notifications via FCM with local notification display',
      'Home screen widget showing pending tasks (iOS/Android)',
      'Stripe checkout and subscription portal sessions',
      'Referral tracking and creator code generation',
    ],
  },
  {
    id: '4', version: '1.4', date: 'Mar 5, 2026', type: 'feature',
    title: 'Mobile API Layer & Firebase Integration',
    description: 'REST API endpoints power the mobile app connected to production Firebase.',
    changes: [
      'REST API Cloud Functions: tasks, grocery, maintenance, calendar, notifications, pets, vehicles, bills',
      'AI Assistant with Gemini integration on both platforms',
      'Daily brief generation with weather data and calendar event summaries',
      'Mobile app fully connected to production Firestore and Auth',
    ],
  },
  {
    id: '3', version: '1.3', date: 'Mar 1, 2026', type: 'feature',
    title: 'Differentiators — Meal Planner, Document Vault & Analytics',
    description: 'Three high-value modules that differentiate EverydayOS from basic task managers.',
    changes: [
      'Meal planner with 7-day AI generation, recipe library, and grocery list export',
      'Document Vault with Firebase Storage upload, tag filtering, and home inventory',
      'Household analytics — 14-day task completion trend, contribution scoring, grocery spend',
      'Google Calendar bidirectional sync with OAuth integration',
    ],
  },
  {
    id: '2', version: '1.2', date: 'Feb 25, 2026', type: 'feature',
    title: 'MVP — Kanban Tasks, Grocery & Contacts',
    description: 'The core household collaboration features: task management, grocery, and contacts.',
    changes: [
      'Drag-and-drop Kanban task board with multi-column layout and priority tiers',
      'Grocery list with category grouping, pantry tracking, and AI categorization',
      'Contacts with service providers, emergency contacts, and maintenance history',
      'Household member management with role-based permissions and invite codes',
    ],
  },
  {
    id: '1', version: '1.0', date: 'Feb 20, 2026', type: 'feature',
    title: 'Foundation — Auth, Dashboard & Household Engine',
    description: 'The architectural foundation: monorepo, Firebase, and the customizable dashboard.',
    changes: [
      'Firebase Auth with Google sign-in and email/password',
      'Customizable dashboard with drag-and-drop widget grid and persistent layouts',
      'Household creation with owner/member/viewer roles and invite code flow',
      'pnpm monorepo with Turborepo 2.x, shared types, UI, logic, and utils packages',
    ],
  },
];

export const Changelog: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const navigate = useNavigate();
  const logs = CHANGELOG;

  const getTypeStyles = (type: string) => {
    const t = type?.toLowerCase();
    switch (t) {
      case 'feature': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'fix': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      default: return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  return (
    <div className={`${embedded ? '' : 'max-w-page-narrow mx-auto p-6'} pb-20 animate-in fade-in duration-700`}>
      {!embedded && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-blue-500 mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-black uppercase tracking-widest">Back to OS</span>
        </button>
      )}

      <div className="flex items-center gap-4 mb-12">
        <div className="p-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[1.5rem] shadow-xl">
          <History className="w-8 h-8" />
        </div>
        <div>
          <p className="text-slate-500 font-medium">Tracking the evolution of EverydayOS</p>
        </div>
      </div>

      <div className="relative space-y-12">
        {/* The Timeline Vertical Rail */}
        <div className="absolute left-6 top-2 bottom-0 w-0.5 bg-slate-100 dark:bg-slate-800" />

        {logs.map((log) => (
          <div key={log.id} className="relative pl-16">
            <div className="absolute left-[21px] top-2 w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700 border-4 border-white dark:border-slate-950 ring-4 ring-slate-50 dark:ring-slate-900/50" />
            
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-8 shadow-sm hover:shadow-md transition-all">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-black text-brand bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-lg">
                    v{log.version}
                  </span>
                  <span className={`text-2xs font-black uppercase tracking-widest px-3 py-1 rounded-lg ${getTypeStyles(log.type)}`}>
                    {log.type ?? 'UPDATE'}
                  </span>
                </div>
                <time className="text-xs font-bold text-slate-400">{log.date}</time>
              </div>

              <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{log.title}</h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed border-b border-slate-50 dark:border-slate-800 pb-6">
                {log.description}
              </p>

              <ul className="space-y-3">
                {(log.changes ?? []).map((change, i) => {
                  // Detect if line is a Category Header (starts with an emoji/non-ASCII)
                  const isHeader = /^[^\x00-\x7F]/.test(change);

                  return (
                    <li 
                      key={i} 
                      className={`flex items-start gap-3 transition-all ${
                        isHeader 
                          ? 'mt-8 mb-2 border-b border-slate-50 dark:border-slate-800 pb-2' 
                          : 'ml-4 opacity-80'
                      }`}
                    >
                      {isHeader ? (
                        <span className="font-black text-slate-900 dark:text-white text-[11px] tracking-[0.2em] uppercase">
                          {change}
                        </span>
                      ) : (
                        <>
                          <Zap className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-1 shadow-sm" />
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed">
                            {change.replace('• ', '').replace('•', '')}
                          </span>
                        </>
                      )}
                    </li>
                  );
                })}
                {(!log.changes || log.changes.length === 0) && (
                  <li className="text-xs italic text-slate-400 tracking-tight">
                    No specific sub-protocols listed for this version.
                  </li>
                )}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};